import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { endpoints, asset } from "@/lib/api";
import { LineReveal, Reveal, ClippedImage, ParallaxImage, Marquee, EASE_EXPRESSIVE } from "@/lib/motion";
import { CTASection, SectionTitle } from "@/components/site/Bits";

const MANIFESTO = [
  { n: "01", t: "We don't build bars. We build atmosphere.", b: "Traditional bars ask guests to leave the conversation. ARIA brings exceptional cocktails directly into it." },
  { n: "02", t: "Every pour is personal.", b: "Every cocktail is prepared, presented and served by hand with warmth, precision and impeccable timing." },
  { n: "03", t: "Hospitality becomes part of the design.", b: "Our service doesn't occupy the room — it completes it, moving seamlessly through the event with elegance and intention." },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] w-full overflow-hidden aria-grain" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={asset("/api/media/gen-hero/raw")}
          alt="An ARIA host serving a sparkling cocktail in a candle-lit ballroom"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-aria-ink/25" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-aria-ink/90 via-aria-ink/40 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 flex min-h-[100svh] flex-col justify-end pt-32 md:pt-40">
        <div className="aria-container pb-20 md:pb-28">
          <h1 className="font-display text-aria-ivory text-[3rem] leading-[0.95] sm:text-6xl md:text-7xl lg:text-8xl xl:text-[8.5rem] tracking-tight">
            <LineReveal lines={["A roaming", "cocktail", "experience."]} delay={0.35} stagger={0.14} />
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.5, ease: EASE_EXPRESSIVE }}
            className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between"
          >
            <p className="max-w-lg text-xl text-aria-ivory/85 leading-relaxed">
              A luxury roaming cocktail experience designed for weddings, private celebrations, embassies, museums, corporate gatherings and unforgettable evenings.
            </p>
            <Link
              to="/book"
              data-testid="hero-book-link"
              className="inline-flex items-center gap-3 border-b border-aria-brass pb-2 font-display text-2xl text-aria-ivory transition-colors hover:text-aria-brass"
            >
              Book the Experience <span aria-hidden>→</span>
            </Link>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 right-6 z-10 hidden items-center gap-3 text-aria-ivory/60 md:flex md:right-12"
      >
        <span className="text-[0.65rem] uppercase tracking-[0.24em]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="h-8 w-px bg-aria-ivory/40" />
      </motion.div>
    </section>
  );
}

export default function Home() {
  const [eventTypes, setEventTypes] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [journal, setJournal] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    endpoints.eventTypes().then(setEventTypes).catch(() => {});
    endpoints.caseStudies().then(setCaseStudies).catch(() => {});
    endpoints.journal().then(setJournal).catch(() => {});
    endpoints.testimonials().then(setTestimonials).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Hero />

      {/* Manifesto */}
      <section className="aria-container py-28 md:py-44" data-testid="manifesto">
        <Reveal>
          <p className="eyebrow mb-14">The ARIA Philosophy</p>
        </Reveal>
        <div className="grid grid-cols-1 gap-16 md:grid-cols-3 md:gap-12">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.12}>
              <div className="border-t border-aria-travertine pt-8">
                <span className="font-accent italic text-5xl md:text-6xl text-aria-brass/60">{m.n}</span>
                <h3 className="mt-8 font-display text-3xl md:text-4xl leading-tight text-aria-ink">{m.t}</h3>
                <p className="mt-5 text-lg leading-relaxed text-aria-cocoa/85">{m.b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Editorial split — the experience */}
      <section className="aria-container pb-28 md:pb-44">
        <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-12 md:gap-20">
          <div className="md:col-span-7">
            <ClippedImage src="/hero-hostesses.png" alt="Two ARIA hosts with vintage soda syphons in an elegant restaurant" aspect="aspect-[16/11]" />
          </div>
          <div className="md:col-span-5">
            <SectionTitle eyebrow="The Experience" title={["Composed", "for the room."]} />
            <Reveal delay={0.2}>
              <p className="mt-8 font-light text-lg leading-relaxed text-aria-cocoa/80">
                Every detail is considered — the attire, the glassware, the rhythm of service. ARIA is designed to move with your evening, never against it.
              </p>
              <Link to="/experience" className="mt-10 inline-block aria-link font-display text-2xl text-aria-ink" data-testid="home-experience-link">
                Discover the ritual
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Photo strip — latest moments */}
      <section className="pb-28 md:pb-44" data-testid="home-moments">
        <div className="aria-container mb-10 flex items-end justify-between gap-6">
          <SectionTitle eyebrow="In the Moment" title={["Scenes from", "the evening."]} />
          <Reveal>
            <Link to="/gallery" className="aria-link text-[0.72rem] uppercase tracking-[0.22em] text-aria-ink/70">
              Enter the gallery
            </Link>
          </Reveal>
        </div>
        <div className="aria-container grid grid-cols-2 gap-3 md:grid-cols-5 md:gap-5">
          {[
            { src: "/api/media/real-06/raw", alt: "ARIA hosts arriving at the bar", aspect: "aspect-[3/4]" },
            { src: "/api/media/real-07/raw", alt: "ARIA host pouring a syphon cocktail", aspect: "aspect-[3/4]" },
            { src: "/api/media/real-08/raw", alt: "A syphon pouring into a coupe", aspect: "aspect-[3/4]" },
            { src: "/api/media/real-09/raw", alt: "Two ARIA hosts raising their syphons", aspect: "aspect-[3/4]" },
            { src: "/api/media/real-10/raw", alt: "ARIA hosts preparing syphon cocktails", aspect: "aspect-[3/4]" },
          ].map((p, i) => (
            <Reveal key={p.src} delay={(i % 5) * 0.08}>
              <div className={`relative overflow-hidden ${p.aspect}`}>
                <img
                  src={asset(p.src)}
                  alt={p.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.76,0,0.24,1)] hover:scale-105"
                />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Marquee */}
      <section className="border-y border-aria-travertine bg-aria-sand py-12 md:py-16">
        <Marquee items={["Weddings", "Embassies", "Museums", "Estates", "Hotels", "Galas"]} />
      </section>

      {/* Event types */}
      <section className="aria-container py-28 md:py-44" data-testid="home-event-types">
        <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <SectionTitle eyebrow="Occasions" title={["For every", "kind of evening."]} />
          <Reveal>
            <Link to="/event-types" className="aria-link text-[0.72rem] uppercase tracking-[0.22em] text-aria-ink/70">
              View all event types
            </Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {eventTypes.slice(0, 6).map((et, i) => (
            <Reveal key={et.id} delay={(i % 3) * 0.1}>
              <Link to={`/event-types/${et.slug}`} className="group block" data-testid={`event-card-${et.slug}`}>
                <div className="relative overflow-hidden aspect-[4/5]">
                  <img
                    src={asset(et.hero_image)}
                    alt={et.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-aria-ink/10 transition-colors duration-500 group-hover:bg-aria-ink/25" />
                </div>
                <h3 className="mt-6 font-display text-3xl text-aria-ink">{et.name}</h3>
                <p className="mt-1 font-light text-aria-muted">{et.tagline}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Case studies feature */}
      {caseStudies[0] && (
        <section className="bg-aria-ink text-aria-ivory" data-testid="home-case-study">
          <div className="aria-container grid grid-cols-1 items-stretch gap-0 md:grid-cols-2">
            <div className="relative min-h-[420px] overflow-hidden md:min-h-[640px]">
              <ParallaxImage src={caseStudies[0].hero_image} alt={caseStudies[0].title} className="absolute inset-0 h-full" strength={40} />
            </div>
            <div className="flex flex-col justify-center py-20 md:pl-20">
              <div className="eyebrow mb-8">Case Study · {caseStudies[0].location}</div>
              <h2 className="font-display text-4xl md:text-6xl leading-[1.02]">{caseStudies[0].title}</h2>
              <p className="mt-8 max-w-md font-light text-aria-ivory/60 leading-relaxed">{caseStudies[0].overview}</p>
              <div className="mt-10 flex gap-10 text-[0.7rem] uppercase tracking-[0.2em] text-aria-brass">
                <span>{caseStudies[0].guest_count}</span>
                <span>{caseStudies[0].client_type}</span>
              </div>
              <Link
                to={`/case-studies/${caseStudies[0].slug}`}
                className="mt-12 inline-block aria-link font-display text-2xl text-aria-ivory"
                data-testid="home-casestudy-link"
              >
                Read the story
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="aria-container py-28 md:py-44" data-testid="home-testimonials">
        {testimonials[0] && (
          <Reveal>
            <blockquote className="mx-auto max-w-4xl text-center">
              <p className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.15] text-aria-ink text-balance">
                “{testimonials[0].quote}”
              </p>
              <footer className="mt-10 text-[0.72rem] uppercase tracking-[0.24em] text-aria-muted">
                {testimonials[0].author} — {testimonials[0].role}
              </footer>
            </blockquote>
          </Reveal>
        )}
      </section>

      {/* Journal teaser */}
      {journal.length > 0 && (
        <section className="aria-container pb-28 md:pb-44" data-testid="home-journal">
          <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <SectionTitle eyebrow="The Journal" title="Notes on hospitality." />
            <Reveal>
              <Link to="/journal" className="aria-link text-[0.72rem] uppercase tracking-[0.22em] text-aria-ink/70">
                Read the journal
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {journal.slice(0, 3).map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.1}>
                <Link to={`/journal/${p.slug}`} className="group block" data-testid={`journal-card-${p.slug}`}>
                  <div className="relative overflow-hidden aspect-[3/2]">
                    <img src={asset(p.cover_image)} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
                  </div>
                  <div className="mt-5 text-[0.68rem] uppercase tracking-[0.22em] text-aria-brass">{p.category} · {p.read_time}</div>
                  <h3 className="mt-3 font-display text-2xl leading-snug text-aria-ink">{p.title}</h3>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <CTASection />
    </div>
  );
}
